import { Button, Divider, Flex, Layout, Skeleton } from 'antd'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import CustomInput from 'src/app/shared/components/common/input'
import CustomSelect from 'src/app/shared/components/common/select'
import CustomSwitch from 'src/app/shared/components/common/switch'
import { ActivationWarning } from 'src/app/shared/components/common/tag/ActivationWarning'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'
import { inputNumber } from 'src/app/shared/utils'
import { getLookupValues } from 'src/app/store/masterSetup'
import ACHForm from '../components/ACHForm'
import CheckForm from '../components/CheckForm'
import CustomConfirmationModal from '../components/CustomConfirmationModal'
import InvalidEditAccountModal from '../components/InvalidEditAccountModal'
import PayeeMatchWarningModal from '../components/PayeeMatchWarningModal'
import '../styles/index.scss'
import { useEditFormRules } from '../utils'
import { useEditAccountContext } from './index.context'

const EditAccountContent: React.FC = () => {
  const { t } = useTranslation()
  const rules = useEditFormRules()
  const lookupValues = useSelector(getLookupValues)
  const {
    form,
    achExceptionProcessingType,
    checkExceptionProcessingType,
    loading,
    modalRef,
    payeeMatchWarningRef,
    isFetched,
    client,
    invalidEditAccountRef,
    setSwitchChanged,
    onSubmit,
  } = useEditAccountContext()
  const { handleSubmit, control, formState } = useMemo(() => form, [form])
  const { errors, isValid, isDirty } = useMemo(() => formState, [formState])
  const navigate = useNavigate()
  const { mode } = useWindowSize()

  const achExceptionType = useWatch({
    control,
    name: 'achExceptionProcessingType',
  })
  const checkExceptionType = useWatch({
    control,
    name: 'checkExceptionProcessingType',
  })

  useEffect(() => {
    const bothNoCreate = achExceptionType === 'NOCREATE' && checkExceptionType === 'NOCREATE'

    if (bothNoCreate) {
      form.setError('achExceptionProcessingType', {
        type: 'manual',
        message: 'At least one exception type (ACH or Check) must be enabled to proceed.',
      })
      form.setError('checkExceptionProcessingType', {
        type: 'manual',
        message: 'At least one exception type (ACH or Check) must be enabled to proceed.',
      })
    } else {
      form.clearErrors(['achExceptionProcessingType', 'checkExceptionProcessingType'])
    }
  }, [achExceptionType, checkExceptionType, form])

  if (!isFetched) {
    return (
      <Layout.Content style={{ padding: '24px 24px 0 24px' }}>
        <Skeleton />
      </Layout.Content>
    )
  }

  return (
    <Layout.Content style={{ padding: '24px 24px 0 24px' }}>
      {client?.activationDate && <ActivationWarning activationDate={dayjs(client.activationDate)} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex
          style={{
            flexDirection: 'column',
            gap: 24,
          }}
        >
          <Controller
            control={control}
            name={'clientName'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.clientName')}
                placeholder={t('AccountSetup:new_account_form.placeholder.clientName')}
                required
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={async (event: any) => {
                  field.onBlur()
                  await form.trigger('clientName')
                }}
                options={[]}
                error={fieldState.error}
                loading={loading}
                disabled
                showSearch
              />
            )}
            rules={rules.clientName}
          />
          <Flex
            style={{
              gap: 24,
              flexDirection: mode === 'mobile' ? 'column' : undefined,
            }}
          >
            <Controller
              control={control}
              name={'accountName'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.accountName')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.accountName')}
                  required
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={async (event) => {
                    field.onBlur()
                    // field.onChange(event?.target?.value?.trim())
                    await form.trigger('accountName')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.accountName.maxLength as any).value}
                />
              )}
              rules={rules.accountName}
            />
            <Controller
              control={control}
              name={'accountNumber'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.accountNumber')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.accountNumber')}
                  required
                  value={field.value}
                  onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                  onBlur={async (event) => {
                    field.onBlur()
                    // field.onChange(event?.target?.value?.replace(/^0+/, ''))
                    await form.trigger('accountNumber')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.accountNumber.maxLength as any).value}
                />
              )}
              rules={rules.accountNumber}
            />
          </Flex>
          <Flex style={{ gap: 24, flexDirection: mode === 'mobile' ? 'column' : undefined }}>
            <Controller
              control={control}
              name={'chargeAccountNumber'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.chargeAccountNumber')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.chargeAccountNumber')}
                  value={field.value}
                  onChange={(e) =>
                    field.onChange(inputNumber(e, (rules.chargeAccountNumber.maxLength as any).value), {
                      shouldValidate: true,
                    })
                  }
                  onBlur={async (event) => {
                    field.onBlur()
                    await form.trigger('accountNumber')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.chargeAccountNumber.maxLength as any).value}
                  required
                />
              )}
              rules={rules.chargeAccountNumber}
            />
            <Controller
              control={control}
              name={'displayNotes'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.displayNotes')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.displayNotes')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                  maxLength={(rules.displayNotes.maxLength as any).value}
                  disabled
                />
              )}
              rules={rules.displayNotes}
            />
          </Flex>

          <Controller
            control={control}
            name={'serviceTypeParameter'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.serviceTypeParameter')}
                placeholder={t('AccountSetup:new_account_form.placeholder.serviceTypeParameter')}
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error}
                options={lookupValues?.SrvChg}
                maxLength={(rules.serviceTypeParameter.maxLength as any).value}
                disabled
                loading={loading}
              />
            )}
            rules={rules.serviceTypeParameter}
          />

          <CheckForm
            checkExceptionProcessingType={checkExceptionProcessingType}
            control={control as any}
            form={form as any}
            loading={loading}
            client={client}
            isEditMode
          />

          <ACHForm
            achExceptionProcessingType={achExceptionProcessingType}
            control={control as any}
            form={form as any}
            loading={loading}
            client={client}
          />

          <Flex
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Controller
              control={form.control}
              name='isArchiveAccount'
              render={({ field, fieldState }) => (
                <CustomSwitch
                  label={t('AccountSetup:new_account_form.title.archiveAccount')}
                  checked={!!field.value}
                  onChange={field.onChange}
                  error={fieldState.error}
                  onClick={() => setSwitchChanged(true)}
                />
              )}
            />
            <Controller
              control={form.control}
              name='createdAt'
              render={({ field, fieldState }) => (
                <CustomInput
                  value={field.value}
                  label={t('AccountSetup:new_account_form.title.createdAt')}
                  onChange={field.onChange}
                  error={fieldState.error}
                  disabled
                />
              )}
            />
          </Flex>
          <Divider />
          <Flex
            style={{
              justifyContent: 'flex-end',
              gap: 24,
            }}
            className='button-group-container'
          >
            <Button
              type='default'
              onClick={() =>
                navigate(menuUrls.accountSetup, {
                  replace: true,
                })
              }
              disabled={loading}
              className='new-account-cancel-button'
            >
              {t('common:cancel')}
            </Button>
            <Button
              type='primary'
              htmlType='button'
              disabled={!isValid || !isEmpty(errors) || !isDirty || loading}
              onClick={handleSubmit(onSubmit)}
              loading={loading}
              className='new-account-button'
            >
              {t('AccountSetup:new_account_form.title.saveChanges')}
            </Button>
          </Flex>
        </Flex>
      </form>
      <CustomConfirmationModal ref={modalRef} />
      <InvalidEditAccountModal ref={invalidEditAccountRef} />
      <PayeeMatchWarningModal ref={payeeMatchWarningRef} />
    </Layout.Content>
  )
}
export default EditAccountContent
